import { Component, OnInit, ElementRef, ViewChild, Input, Output, EventEmitter, signal, effect, input, SimpleChanges } from '@angular/core';
import { FormElementComponent } from '../form-element/form-element.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AddressData } from '../../../models/location'
import { Address } from 'src/app/shared/models/address';

@Component({
    selector: 'app-address-input',
    templateUrl: './address-input.component.html',
    styleUrl: './address-input.component.scss'
})
export class AddressInputComponent extends FormElementComponent implements OnInit {

    @Input() city: string;
    @Input() province: string;
    @Input() address: Address | AddressData;
    @Input() inputBgColor: string | null = null;
    @Input() inputTextColor: string | null = null;

    citySignal = signal<string>(null);
    provinceSignal = signal<string>(null);
    addressSignal = signal<Address | AddressData>(null);

    @Input() override form: FormGroup;
    @Input() maxLength!: number;
    @Input() clearable = false;
    @Input() selectAllOnClick: boolean = false;
    @Input() getCheckState: boolean = null;
    citySelected: string = null
    provinceSelected: string = null
    addressSelected: Address | AddressData
    street: string;
    streetNumber: string;
    fullAddress: string;

    @Output() changes = new EventEmitter();
    @Output() selected = new EventEmitter<AddressData>();
    @Output() addressWithoutNumber = new EventEmitter<boolean>();
    @ViewChild('textInput') textInput!: ElementRef;
    @ViewChild('addressinput', { static: false }) addressinputRef: ElementRef<HTMLInputElement>;
    public readonly ADDRESS_STREET = 'street';
    public readonly ADDRESS_WITHOUT_NUMBER = 'withoutNumber';
    private autocomplete!: google.maps.places.Autocomplete;
    private _isAddressValid: boolean = true;
    private _enabledForm: boolean = false;
    private _selectedAddress: AddressData;
    private selectedValidAddress: string | null = null;
    private lastLocation = { city: null, province: null };
    private ignoreNextAddress = false;

    set enabledForm(value: boolean) {
        this._enabledForm = value;

        const streetCtrl = this.form.get(this.ADDRESS_STREET)!;
        const withoutNumCtrl = this.form.get(this.ADDRESS_WITHOUT_NUMBER)!;

        if (value) {
            // habilitamos sólo los campos de la dirección principal
            streetCtrl.enable({ emitEvent: false });
            // el checkbox “sin número” lo habilitamos o deshabilitamos
            if (this.selectedAddress?.streetNumber === '') {
                withoutNumCtrl.enable({ emitEvent: false });
            } else {
                withoutNumCtrl.disable({ emitEvent: false });
            }
        } else {
            // cuando falte city o province, sólo deshabilitamos la calle y el checkbox
            streetCtrl.disable({ emitEvent: false });
            withoutNumCtrl.disable({ emitEvent: false });
        }
    }


    get enabledForm() {
        return this._enabledForm
    }

    set isAddressValid(value: boolean) {
        this._isAddressValid = value
        const control = this.form.get(this.ADDRESS_STREET);
        if (value) {
            control?.setErrors(null);
        } else {
            control?.setErrors({ invalid: true });
        }
    }

    get isAddressValid() {
        return this._isAddressValid
    }

    set selectedAddress(value: AddressData) {
        this._selectedAddress = value;


        /*         // Dentro de set selectedAddress(value: AddressData) { … }
                const full = [value.street, value.streetNumber]
                    .filter(v => v)
                    .join(' ');

                // 1) Ponés el valor en el form
                this.form.get(this.ADDRESS_STREET)!.setValue(full);
                // 2) Limpiás errores
                this.form.get(this.ADDRESS_STREET)!.setErrors(null);
                // 3) Definís el nuevo “valor bueno” para comparación futura
                this.selectedValidAddress = full; */

        // Nuevo: calcular el booleano sin número
        const withoutNumber = value?.streetNumber === '' || value?.streetNumber === null || value?.streetNumber === undefined;

        const withoutNumberControl = this.form?.get?.(this.ADDRESS_WITHOUT_NUMBER);
        if (withoutNumberControl) {
            if (withoutNumber) {
                // Sin número: habilitamos y forzamos el validador requiredTrue
                withoutNumberControl.enable();
                withoutNumberControl.setValidators(Validators.requiredTrue);
                withoutNumberControl.setValue(true);
                withoutNumberControl.markAsTouched();
                withoutNumberControl.updateValueAndValidity();
            } else {
                // Con número: eliminamos validadores y errores y dejamos el valor real en false
                withoutNumberControl.enable();
                withoutNumberControl.clearValidators();
                withoutNumberControl.setErrors(null);
                withoutNumberControl.markAsPristine();
                withoutNumberControl.markAsUntouched();
                withoutNumberControl.setValue(false);
                withoutNumberControl.updateValueAndValidity();
                withoutNumberControl.disable();
            }
        }

        this.addressWithoutNumber.emit(withoutNumber);

        this.selected.emit(value);
    }

    get selectedAddress() {
        return this._selectedAddress;
    }
    get patientStreet() {
        return this.form.get(this.ADDRESS_STREET).value;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['address'] && this.address) {
            // 1) Setea directamente el formControl
            const addr = this.address as AddressData;
            const full = [addr.street, addr.streetNumber].filter(Boolean).join(' ');
            const control = this.form.get(this.ADDRESS_STREET)!;

            control.setValue(full);
            this.form.get(this.ADDRESS_STREET)?.setValue(full);
            this.form.get(this.ADDRESS_WITHOUT_NUMBER)?.setValue(!addr.streetNumber);
            // 2) Guarda la dirección válida como referencia para validaciones
            this.selectedValidAddress = full;

            // 3) También actualiza el signal para tu effect, si lo necesitás
            this.addressSignal.set(addr);

            const errs = control.errors || {};
            delete errs['invalid'];
            if (Object.keys(errs).length) {
                control.setErrors(errs);
            } else {
                control.setErrors(null);
            }
            // re-ejecutar validadores (required, pattern, etc) si hacen falta
            control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        }

        if (changes['city']) this.citySignal.set(this.city);
        if (changes['province']) this.provinceSignal.set(this.province);
    }

    constructor(private fb: FormBuilder) {
        super();

        effect(() => {
            const city = this.citySignal();
            const province = this.provinceSignal();
            const address = this.addressSignal();

            // habilito form
            this.enabledForm = !!city && !!province;

            // sólo reseteo si es un cambio *posterior*
            if (city && province &&
                (city !== this.lastLocation.city || province !== this.lastLocation.province) &&
                this.lastLocation.city !== null) {
                this.ignoreNextAddress = true;

                const control = this.form.get(this.ADDRESS_STREET)!;
                control.setValue('');
                control.setErrors(null);
                control.markAsPristine();
                control.markAsUntouched();

                this.form.get(this.ADDRESS_STREET)?.setValue('');
                this.form.get(this.ADDRESS_WITHOUT_NUMBER)?.setValue(false);
                this.selectedAddress = null;
                this.setCityBounds(city, province);
            }

            const ctrl = this.form.get(this.ADDRESS_STREET)!;
            const errs2 = ctrl.errors || {};
            delete errs2['invalid'];
            if (Object.keys(errs2).length) {
                ctrl.setErrors(errs2);
            } else {
                ctrl.setErrors(null);
            }
            ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });

            // inicializo bounds (primera vez) o vuelvo a reflejar address
            if (city && province) {
                this.setCityBounds(city, province);
            }
            if (address && city && province && !this.ignoreNextAddress) {
                // vuelco address al form
                const full = [address.street, address.streetNumber].filter(Boolean).join(' ');
                this.form.get(this.ADDRESS_STREET)?.setValue(full);
                this.form.get(this.ADDRESS_WITHOUT_NUMBER)?.setValue(!address.streetNumber);
                this.selectedAddress = address as AddressData;
            }

            this.lastLocation = { city, province };
            this.ignoreNextAddress = false;
        });

    }

    override ngOnInit(): void {

        // Suscribirse a los cambios en el input de dirección para validarlo
        this.form.get(this.ADDRESS_STREET)?.valueChanges.subscribe((value) => {
            const control = this.form.get(this.ADDRESS_STREET);
            if (control?.dirty) {
                // Si el campo está sucio y el valor no coincide con el valor original seleccionado, marca el error
                if (value !== this.selectedValidAddress) {
                    control.setErrors({ invalid: true });
                } else {
                    control.setErrors(null);
                }
            }
        });

        this.form.get(this.ADDRESS_WITHOUT_NUMBER)?.valueChanges.subscribe(value => {
            if (value == true || value == false) {
                this.addressWithoutNumber.emit(value)
            }
        });
    }


    ngAfterViewInit(): void {
        this.initializeAutocomplete();
        this.autocomplete.addListener('place_changed', () => {
            const place = this.autocomplete.getPlace();
            /*             const comps = place.address_components || []; */

            // 1) Usás tu helper para extraer y limpiar todos los campos
            const addr = this.getAddressComponents(place);

            // 2) Armás la dirección completa “Calle Nº”
            const full = [addr.street, addr.streetNumber]
                .filter(v => v != null && v !== '')
                .join(' ');

            // 3) Seteás el form y limpiás sólo el error de “invalid”
            const ctrl = this.form.get(this.ADDRESS_STREET)!;
            ctrl.setValue(full);
            // sólo quitás invalidAddress / invalid, sin tocar otros errores
            const errs = ctrl.errors || {};
            delete errs['invalid'];
            if (Object.keys(errs).length) ctrl.setErrors(errs);
            else ctrl.setErrors(null);
            ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });

            this.selectedValidAddress = full;

            // 4) Checkbox “sin número”
            this.form.get(this.ADDRESS_WITHOUT_NUMBER)!.setValue(!addr.streetNumber);

            // 5) Disparás tu setter para validaciones adicionales y emit
            this.selectedAddress = addr;
            this.selected.emit(addr);
        });
    }

    private initializeAutocomplete(): void {
        const options = {
            types: ['address'], // Limita los resultados a direcciones
            componentRestrictions: { country: 'ar' }, // Restricción a Argentina
            fields: ['address_components', 'geometry'], // Limita los campos devueltos
            strictBounds: true, // Habilita límites estrictos
        };

        // Configuramos Autocomplete
        this.autocomplete = new google.maps.places.Autocomplete(this.addressinputRef.nativeElement, options);
    }

    selectAll() {
        if (this.selectAllOnClick) {
            const input = this.textInput.nativeElement;
            input.select();
        }
    }

    getError() {
        const control = this.form.get(this.name);
        if (control && control.errors) {
            return this.objectValues(control.errors)[0];
        }
        return null;
    }

    hasError(errorType: string): boolean {
        const control = this.form.get(this.name);
        return control && control.hasError(errorType) && (control.dirty || control.touched);
    }

    // Función para mapear los componentes de la dirección
    private getAddressComponents(place: google.maps.places.PlaceResult): AddressData {
        let street = '';
        let streetNumber = '';
        let city = '';
        let province = '';
        let country = '';

        place.address_components?.forEach(comp => {
            const type = comp.types[0];
            switch (type) {
                case 'route': street = comp.long_name; break;
                case 'street_number': streetNumber = comp.long_name; break;
                case 'locality': city = comp.long_name.replace('Ciudad de ', '').trim(); break;
                case 'administrative_area_level_1':
                    province = comp.long_name.replace('Provincia de ', '').trim();
                    break;
                case 'country': country = comp.long_name; break;
            }
        });

        const latitude = place.geometry?.location.lat();
        const longitude = place.geometry?.location.lng();

        return { street, streetNumber, city, province, country, latitude, longitude };
    }

    // Función para obtener las coordenadas de una ciudad y provincia
    private async setCityBounds(city: string, province: string): Promise<void> {
        if (!this.autocomplete) return;

        const geocoder = new google.maps.Geocoder();
        const norm = (s: string | null | undefined) =>
          (s ?? '').toString().trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        const pick = (r: google.maps.GeocoderResult, type: string) =>
          (r.address_components ?? []).find(c => c.types.includes(type))?.long_name ?? null;

        const cityN = norm(city);
        const provN = norm(province);

        const applyBoundsFromResult = (r: google.maps.GeocoderResult) => {
          const vp = r.geometry?.viewport;
          if (vp) {
            this.autocomplete.setBounds(vp);
            this.autocomplete.setOptions({ strictBounds: true });
            return;
          }
          const loc = r.geometry?.location;
          if (!loc) return;

          const bounds = new google.maps.LatLngBounds();
          const offset = 0.1;
          bounds.extend(new google.maps.LatLng(loc.lat() - offset, loc.lng() - offset));
          bounds.extend(new google.maps.LatLng(loc.lat() + offset, loc.lng() + offset));
          this.autocomplete.setBounds(bounds);
          this.autocomplete.setOptions({ strictBounds: true });
        };

        try {
          // 1) intento normal
          const address = `${city}, ${province}, Argentina`;
          let { results } = await geocoder.geocode({ address });

          if (!results?.length) return;

          const best = results[0];

          const bestIsRoute = (best.types ?? []).includes('route');
          const bestLocality = norm(pick(best, 'locality'));
          const bestAdmin1 = norm(pick(best, 'administrative_area_level_1'));

          const looksWrong =
            bestIsRoute ||
            (bestLocality && bestLocality !== cityN) ||
            (bestAdmin1 && !bestAdmin1.includes(provN));

          // 2) si el geocoder “se confundió” (tu caso), reintento fuerte
          if (looksWrong) {
            const fallback = await geocoder.geocode({
              address: `${city}, Argentina`,
              componentRestrictions: {
                country: 'AR',
                administrativeArea: province,
              } as any,
            } as any);

            results = fallback.results ?? [];

            // elegí el primer resultado que NO sea calle/establecimiento
            const better =
              results.find(r => !((r.types ?? []).includes('route') || (r.types ?? []).includes('street_address')))
              ?? results[0];

            if (better) {
              applyBoundsFromResult(better);
              return;
            }
          }

          // si no estaba mal, usá el primero como venías
          applyBoundsFromResult(best);
        } catch (error) {
          console.error('Error al geocodificar la dirección:', error);
        }
      }

}
